import { cn, severityStyle, SeverityOrNull, type Severity } from '../../utils';
import { Icon } from '../../components/icons/Icon';
import { ValidationTooltip, type ValidationTooltipIssue } from './ValidationTooltip';
import type { TooltipPosition } from '../../types/TooltipPosition';

export type { Severity };

export interface ValidationSeverityIconProps {
  severity: SeverityOrNull;
  /** Extra classes applied to the outermost element (icon or tooltip wrapper) */
  className?: string;
  size?: 'sm' | 'md';
  /**
   * When provided the icon becomes a hover tooltip trigger listing these issues.
   * Each row is rendered on its own line with the row's own severity icon.
   * Messages are displayed as-is (the caller is expected to provide localised strings).
   */
  issues?: ValidationTooltipIssue[];
  /**
   * Simple raw-string tooltip — use instead of `issues` when you already have
   * a pre-formatted summary (e.g. "3 items: 2 errors, 1 warning").
   */
  label?: string;
  /** Tooltip arrow direction — defaults to 'top' */
  tooltipPosition?: TooltipPosition;
}

/**
 * Renders a small severity indicator icon (✕ error / ⚠ warning).
 *
 * Pass `issues` to get a hover tooltip with one row per validation message
 * (each row uses its own severity color). The outer tooltip uses the
 * primary severity's color treatment.
 */
export function ValidationSeverityIcon({
  severity,
  className,
  size = 'md',
  issues,
  label,
  tooltipPosition = 'top'
}: ValidationSeverityIconProps) {
  const style = severityStyle(severity);
  if (!style) return null;

  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const iconEl = (
    <Icon name={style.icon} className={cn(sizeClass, style.iconColor)} aria-hidden='true' />
  );

  if (!label && !issues?.length) {
    return (
      <Icon
        name={style.icon}
        className={cn(sizeClass, style.iconColor, className)}
        aria-hidden='true'
      />
    );
  }

  return (
    <ValidationTooltip
      severity={severity as Severity}
      label={label}
      issues={issues}
      position={tooltipPosition}
      className={className}
    >
      {iconEl}
    </ValidationTooltip>
  );
}

ValidationSeverityIcon.displayName = 'ValidationSeverityIcon';
